import React, { useCallback, useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";
import { faDownload } from "@fortawesome/pro-regular-svg-icons/faDownload";

import { useFormFileTransport } from "../DocumentView/FormFileTransportContext";

/** What a renderer hands the lightbox when the user clicks an image. */
export interface LightboxImage {
  fileId: number;
  name: string | null;
  /** The already-loaded small preview — shown instantly while the big one loads. */
  previewUrl: string | null;
  /** Full-res original, offered as "download original". */
  fullUrl: string | null;
  /** True for a design-time image (DisplayImage) vs an uploaded answer file. */
  design?: boolean;
}

/** Pick a sensible target width for the viewport (device-pixel aware, clamped). */
function viewportImageWidth(): number {
  if (typeof window === "undefined") return 1600;
  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(window.innerWidth * dpr);
  // media-worker clamps to [32, 4096]; keep a floor so tiny windows still zoom.
  return Math.max(800, Math.min(4096, w));
}

const Lightbox: React.FC<{
  image: LightboxImage;
  onClose: () => void;
  /** What had focus when the preview opened; focus returns there on close. */
  opener: HTMLElement | null;
}> = ({ image, onClose, opener }) => {
  const transport = useFormFileTransport();
  const [bigUrl, setBigUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Resolve the viewport-sized image on open. Falls back to the full-res
  // original if the host has no image service or it returns nothing.
  useEffect(() => {
    let cancelled = false;
    setBigUrl(null);
    setFailed(false);
    const width = viewportImageWidth();
    const fetchSized = transport.fetchSizedUrl;
    const run = fetchSized
      ? fetchSized(image.fileId, width, { design: image.design })
      : Promise.resolve<string | null>(null);
    run
      .then((url) => {
        if (!cancelled) setBigUrl(url ?? image.fullUrl ?? null);
      })
      .catch(() => {
        if (!cancelled) setBigUrl(image.fullUrl ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [transport, image.fileId, image.design, image.fullUrl]);

  const shownSrc = bigUrl ?? image.previewUrl ?? undefined;
  const loading = !bigUrl && !failed;

  // A Radix dialog, so it stacks on a host modal (the fill dialog) as a nested
  // layer: Escape and a backdrop click close the preview only, focus stays in
  // it, and the z-index clears the host's overlay the way the popover does.
  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[10050] bg-black/80 backdrop-blur-sm" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            closeRef.current?.focus();
          }}
          // Radix closes the preview; keep the key from a non-Radix host
          // modal (MUI listens through React), which would close with it.
          onEscapeKeyDown={(e) => e.stopPropagation()}
          // No Dialog.Trigger here, so Radix has nowhere to send focus back to.
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (opener?.isConnected) opener.focus();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="fixed inset-0 z-[10050] flex items-center justify-center p-4 outline-none"
        >
          <DialogPrimitive.Title className="sr-only">
            {image.name || "Image preview"}
          </DialogPrimitive.Title>
          {/* Controls */}
          <div className="absolute right-3 top-3 flex items-center gap-2">
            {image.fullUrl && (
              <a
                href={image.fullUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex h-9 items-center gap-2 rounded-full bg-white/10 px-3 text-[13px] font-semibold text-white transition-colors hover:bg-white/20"
                title="Download original"
              >
                <FontAwesomeIcon icon={faDownload} style={{ fontSize: 14 }} />
                Original
              </a>
            )}
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faXmark} style={{ fontSize: 18 }} />
            </button>
          </div>

          {shownSrc ? (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <img
              src={shownSrc}
              alt={image.name || "Image"}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[92vh] max-w-[92vw] rounded-md bg-white object-contain shadow-2xl"
            />
          ) : (
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-white/80"
              style={{ fontSize: 28 }}
            />
          )}

          {/* Loading spinner overlaid while the big image resolves over the preview. */}
          {loading && shownSrc && (
            <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin text-white/80"
                style={{ fontSize: 20 }}
              />
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

/**
 * Click-to-zoom lightbox for form images. Returns an `openImage` callback to
 * wire onto a preview `<img>`, and `lightbox` — the modal node (null when
 * closed) — to drop into the renderer's tree. On open it asks the host for a
 * viewport-sized image via `transport.fetchSizedUrl`, showing the small
 * preview instantly underneath.
 */
export function useImageLightbox(): {
  openImage: (image: LightboxImage) => void;
  lightbox: React.ReactNode;
} {
  const [image, setImage] = useState<LightboxImage | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const openImage = useCallback((img: LightboxImage) => {
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement ? active : null;
    setImage(img);
  }, []);
  const close = useCallback(() => setImage(null), []);
  const canPortal = typeof document !== "undefined";
  return {
    openImage,
    lightbox:
      image && canPortal ? (
        <Lightbox image={image} onClose={close} opener={openerRef.current} />
      ) : null,
  };
}
